import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/app/providers/AuthProvider";
import { resolvePortalUserId } from "@/features/merchant/hooks/useUpdateProfile";
import {
  type AddShipmentCommentPayload,
  addShipmentComment,
  createCustomOrderV4,
  type EditCommentPayload,
  editComment,
  type GetOrdersV4Params,
  getOrdersV4,
  getOrderV4,
  type ListShipmentCommentsParams,
  listShipmentComments,
  type RequestCollectionReattemptPayload,
  requestCollectionReattempt,
  type UpdateOrderV4Payload,
  updateCustomOrderV4,
} from "../api/ordersV4Api";
import { isOrdersTableListQuery } from "../ordersListQuery";

/** Paginated V4 orders list. */
export function useOrdersV4(params: GetOrdersV4Params, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["orders-v4", "list", params],
    queryFn: () => getOrdersV4(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

/** Single V4 order detail. */
export function useOrderV4(orderId: string | null) {
  return useQuery({
    queryKey: ["orders-v4", "detail", orderId],
    queryFn: () => getOrderV4(orderId!),
    enabled: !!orderId,
  });
}

/** Create a custom V4 order. Invalidates the list; the caller navigates using the returned `_id`. */
export function useCreateCustomOrderV4() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustomOrderV4,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["orders-v4", "list"] });
      void queryClient.invalidateQueries({ predicate: isOrdersTableListQuery });
    },
  });
}

/** Edit a V4 order. Invalidates the detail + list so the refetch shows fresh data. */
export function useUpdateOrderV4() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateOrderV4Payload) => updateCustomOrderV4(payload),
    onSuccess: (_data, payload) => {
      void queryClient.invalidateQueries({ queryKey: ["orders-v4", "detail", payload.orderId] });
      void queryClient.invalidateQueries({ queryKey: ["orders-v4", "list"] });
      void queryClient.invalidateQueries({ predicate: isOrdersTableListQuery });
    },
  });
}

/** Reinstate a Collection Failed shipment (admin). orderId is only for invalidation. */
export function useRequestCollectionReattempt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RequestCollectionReattemptPayload & { orderId: string }) =>
      requestCollectionReattempt(payload),
    onSuccess: (_data, payload) => {
      void queryClient.invalidateQueries({ queryKey: ["orders-v4", "detail", payload.orderId] });
      void queryClient.invalidateQueries({ queryKey: ["orders-v4", "list"] });
      void queryClient.invalidateQueries({ predicate: isOrdersTableListQuery });
    },
  });
}

/**
 * One key source for the read and its invalidation, so the two cannot drift apart.
 * `identity` scopes the cache to the signed-in user AND role — the backend redacts
 * per role, so an Admin's rows must never be served to whoever logs in next.
 */
export function shipmentCommentsQueryKey(params?: ListShipmentCommentsParams, identity?: string) {
  return params ? ["orders-v4", "comments", identity ?? "anon", params] : ["orders-v4", "comments"];
}

/** Shipment comments — one shipment (shipmentId/waybill) or an order roll-up (orderId). */
export function useShipmentComments(
  params: ListShipmentCommentsParams,
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  const { user } = useAuth();
  const identity = `${resolvePortalUserId(user) ?? "anon"}:${user?.role ?? "none"}`;
  // `refetchInterval: false` (ProblemSolveConversationCard/ProblemSolveNotifier,
  // per direct instruction) means manual-refresh-only — so every OTHER
  // automatic refetch trigger (window refocus, network reconnect, remount)
  // is turned off too, not just the timer, otherwise the thread would
  // still silently update itself without a click. OrderCommentsCard
  // doesn't opt into this and keeps every default untouched.
  const manualOnly = options?.refetchInterval === false;
  return useQuery({
    queryKey: shipmentCommentsQueryKey(params, identity),
    queryFn: () => listShipmentComments(params),
    staleTime: 30_000,
    // Polls while mounted so a comment posted by another party shows up
    // without a manual reload — paused automatically when the tab isn't
    // focused (refetchIntervalInBackground defaults to false).
    // OrderCommentsCard (this hook's main caller) keeps the 1.5s default.
    // ProblemSolveConversationCard and ProblemSolveNotifier both override
    // this to `false` (manual-refresh-only / fetch once per page visit,
    // per direct instruction) — see each one's own comment for why.
    refetchInterval: options?.refetchInterval ?? 1_500,
    refetchOnWindowFocus: manualOnly ? false : undefined,
    refetchOnReconnect: manualOnly ? false : undefined,
    refetchOnMount: manualOnly ? false : undefined,
    enabled:
      (options?.enabled ?? true) && Boolean(params.shipmentId || params.waybill || params.orderId),
  });
}

/** Post a shipment comment — Admin/Merchant/StoreManager may all post (the
 *  backend derives author_party from role and scopes/redacts accordingly).
 *  Invalidates every comments query, roll-ups included. */
export function useAddShipmentComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddShipmentCommentPayload) => addShipmentComment(payload),
    // No idempotency key on the write — a retried lost response would post the comment twice.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: shipmentCommentsQueryKey() });
      // A comment can move the Problem Solve "Tagged" stat card's count —
      // either by tagging an admin (goes up) or by the commenting admin
      // replying on a shipment they were tagged on (clears theirs). Matches
      // useAddToProblemSolveV4's own invalidation of this same key.
      void queryClient.invalidateQueries({ queryKey: ["problemSolve", "queue"] });
    },
  });
}

/** Edit a comment (admin). Invalidates every comments query so the roll-ups pick the edit up. */
export function useEditComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: EditCommentPayload) => editComment(payload),
    // A CAS on the pre-image backs this write — a retry would only race itself into a 409.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: shipmentCommentsQueryKey() });
    },
  });
}
